import type { GraphGroup } from './matcher.js';
import { FORCE_DEFAULTS, type GraphSettings } from './settings.js';
import { Icon } from '../mo/Icon.js';

/** Collapsible controls overlay: filters, color groups, display + force
 * sliders, timelapse. Floating card top-right on desktop; bottom sheet on
 * small screens (see graph.css). */

const GROUP_PALETTE = [
  '#f38ba8',
  '#f9e2af',
  '#a6e3a1',
  '#89b4fa',
  '#f5a97f',
  '#cba6f7',
  '#94e2d5',
];

const normalizeHex = (c: string): string =>
  /^#[0-9a-fA-F]{6}$/.test(c.trim()) ? c.trim() : '#8b7cf6';

export interface GraphControlsProps {
  mode: 'global' | 'local';
  open: boolean;
  onToggle: () => void;
  query: string;
  onQuery: (q: string) => void;
  settings: GraphSettings;
  onSettings: (patch: Partial<GraphSettings>) => void;
  groups: GraphGroup[];
  onGroups: (groups: GraphGroup[]) => void;
  timelapsePlaying: boolean;
  onTimelapseStart: () => void;
}

export function GraphControls(props: GraphControlsProps) {
  const { settings, onSettings, groups, onGroups } = props;

  if (!props.open) {
    return (
      <button
        className="graph-overlay-btn"
        title="Graph settings"
        aria-label="Graph settings"
        onClick={props.onToggle}
      ><Icon name="settings" size={15} /></button>
    );
  }

  const updateGroup = (i: number, patch: Partial<GraphGroup>) =>
    onGroups(groups.map((g, gi) => (gi === i ? { ...g, ...patch } : g)));

  return (
    <div className="graph-controls">
      <div className="graph-controls-head">
        <span>Graph settings</span>
        <button
          className="graph-close-btn"
          aria-label="Close graph settings"
          onClick={props.onToggle}
        ><Icon name="close" size={15} /></button>
      </div>
      <div className="graph-controls-body">
        <div className="graph-section">
          <div className="graph-section-title">Filters</div>
          <input
            className="graph-search"
            type="search"
            placeholder='Search (tag:, path:, "phrase")'
            value={props.query}
            onChange={(e) => props.onQuery(e.target.value)}
          />
          <Toggle
            label="Orphans"
            checked={settings.showOrphans}
            onChange={(v) => onSettings({ showOrphans: v })}
          />
          <Toggle
            label="Unresolved links"
            checked={settings.showUnresolved}
            onChange={(v) => onSettings({ showUnresolved: v })}
          />
          <Toggle
            label="Attachments"
            checked={settings.showAttachments}
            onChange={(v) => onSettings({ showAttachments: v })}
          />
          {props.mode === 'local' && (
            <div className="graph-field">
              <span>Depth</span>
              <DepthStepper
                value={settings.depth}
                onChange={(d) => onSettings({ depth: d })}
              />
            </div>
          )}
        </div>

        <div className="graph-section">
          <div className="graph-section-title">Groups</div>
          {groups.length === 0 && (
            <div className="graph-hint muted">Color nodes by query — first match wins.</div>
          )}
          {groups.map((g, i) => (
            <div className="graph-group-row" key={i}>
              <input
                type="color"
                aria-label="Group color"
                value={normalizeHex(g.color)}
                onChange={(e) => updateGroup(i, { color: e.target.value })}
              />
              <input
                type="text"
                aria-label="Group query"
                placeholder="tag:… path:… text"
                value={g.query}
                onChange={(e) => updateGroup(i, { query: e.target.value })}
              />
              <button
                className="graph-close-btn"
                aria-label="Remove group"
                onClick={() => onGroups(groups.filter((_g, gi) => gi !== i))}
              ><Icon name="close" size={15} /></button>
            </div>
          ))}
          <button
            className="graph-text-btn"
            onClick={() =>
              onGroups([
                ...groups,
                { query: '', color: GROUP_PALETTE[groups.length % GROUP_PALETTE.length] },
              ])
            }
          >
            + Add group
          </button>
        </div>

        <div className="graph-section">
          <div className="graph-section-title">Display</div>
          <Slider
            label="Text fade threshold"
            min={-3}
            max={3}
            step={0.1}
            value={settings.textFade}
            onChange={(v) => onSettings({ textFade: v })}
          />
        </div>

        <div className="graph-section">
          <div className="graph-section-title">Forces</div>
          <Slider
            label="Center force"
            min={0}
            max={1}
            step={0.01}
            value={settings.centerForce}
            onChange={(v) => onSettings({ centerForce: v })}
          />
          <Slider
            label="Repel force"
            min={0}
            max={20}
            step={0.1}
            value={settings.repelForce}
            onChange={(v) => onSettings({ repelForce: v })}
          />
          <Slider
            label="Link force"
            min={0}
            max={1}
            step={0.01}
            value={settings.linkForce}
            onChange={(v) => onSettings({ linkForce: v })}
          />
          <Slider
            label="Link distance"
            min={30}
            max={500}
            step={1}
            value={settings.linkDistance}
            onChange={(v) => onSettings({ linkDistance: v })}
          />
          <button className="graph-text-btn" onClick={() => onSettings({ ...FORCE_DEFAULTS })}>
            Reset forces
          </button>
        </div>

        {props.mode === 'global' && (
          <div className="graph-section">
            <button
              className="graph-text-btn accent"
              disabled={props.timelapsePlaying}
              onClick={props.onTimelapseStart}
            >
              ▶ Timelapse
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="graph-toggle">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      {props.label}
    </label>
  );
}

function Slider(props: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="graph-slider">
      <span className="graph-slider-head">
        <span>{props.label}</span>
        <span className="muted">{Math.round(props.value * 100) / 100}</span>
      </span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onChange={(e) => props.onChange(Number(e.target.value))}
      />
    </label>
  );
}

export function DepthStepper(props: {
  value: number;
  onChange: (v: number) => void;
  small?: boolean;
}) {
  return (
    <div className={`graph-stepper${props.small ? ' small' : ''}`}>
      <button
        aria-label="Decrease depth"
        disabled={props.value <= 1}
        onClick={() => props.onChange(Math.max(1, props.value - 1))}
      >
        −
      </button>
      <span className="graph-stepper-value">{props.value}</span>
      <button
        aria-label="Increase depth"
        disabled={props.value >= 3}
        onClick={() => props.onChange(Math.min(3, props.value + 1))}
      >
        +
      </button>
    </div>
  );
}
